import React from "react";
import { humanize } from "../../utils/format";

export type Tone = "green" | "amber" | "orange" | "red" | "blue" | "violet" | "slate";

const tones: Record<Tone, string> = {
    green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    orange: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    blue: "bg-sky-500/10 text-sky-400 border-sky-500/20",
    violet: "bg-[#8b5cf6]/10 text-[#a78bfa] border-[#8b5cf6]/20",
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/20",
};

const dots: Record<Tone, string> = {
    green: "bg-emerald-400",
    amber: "bg-amber-400",
    orange: "bg-orange-400",
    red: "bg-red-400",
    blue: "bg-sky-400",
    violet: "bg-[#a78bfa]",
    slate: "bg-slate-400",
};

export default function Badge({ tone = "slate", dot, children }: { tone?: Tone; dot?: boolean; children: React.ReactNode }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border whitespace-nowrap ${tones[tone]}`}
        >
            {dot && <span className={`w-1.5 h-1.5 rounded-full ${dots[tone]}`} />}
            {children}
        </span>
    );
}

/** Colour for every enum value used in the app (matches your sheet: active green, suspended orange, cancelled red) */
const STATUS_TONES: Record<string, Tone> = {
    // product status
    ACTIVE: "green",
    SUSPENDED: "orange",
    CANCELLED: "red",
    PENDING: "slate",
    // payment
    PAID: "green",
    PARTIALLY_PAID: "amber",
    NOT_PAID: "red",
    // delivery
    DELIVERED: "green",
    IN_PROGRESS: "blue",
    // product type
    HARDWARE: "blue",
    SOFTWARE: "violet",
    // roles
    SUPERADMIN: "violet",
    RESELLER: "blue",
    DEVELOPER: "amber",
    USER: "slate",
};

export function StatusBadge({ value }: { value: string | null | undefined }) {
    if (!value) return <span className="text-slate-600">—</span>;
    return (
        <Badge tone={STATUS_TONES[value] ?? "slate"} dot>
            {humanize(value)}
        </Badge>
    );
}